/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState } from 'react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import { BundleConfig } from 'modules/billing/components/FactorialF25Bundles/types'
import {
  getFloorByCountry,
  getPlanByIndustry,
  getPlanTypeByCountry,
  getSelfServedByCountry,
} from 'modules/billing/lib/planCustomizations'
import { ChargebeeSubscription } from 'modules/billing/lib/prices'
import type Plan from 'modules/onboarding/lib/pricing/plans/Plan'
import getF25Plans, { F25PlanDTO } from 'modules/onboarding/lib/pricing/plans/getF25Plans'
import type { VariantsEnum } from 'modules/onboarding/lib/pricing/plans/types'
import {
  PRO_PLAN_TYPE,
  YEAR_PERIOD,
  capitalizeFirstLetter,
} from 'modules/onboarding/lib/pricing/plans/utils'
import { PeriodUnit } from 'modules/onboarding/lib/pricing/types'

import { useDatoPlans } from '../DatoPlansContext'

type F25PricingContextType = {
  allPlans: F25PlanDTO
  togglePlanSelect: (plan: Plan) => void
  setVariantForPlan: (variant: VariantsEnum, plan: Plan) => void
  seats: number
  setSeats: (seats: number) => void
  period: PeriodUnit
  setPeriod: (period: PeriodUnit) => void
  configPlan: BundleConfig
  setConfigPlan: (configPlan: BundleConfig) => void
  isYearly: () => boolean
  computePerSeatPrice: () => number
  computeTotalPrice: () => number
  getSubscriptionItems: () => ChargebeeSubscription[]
  countryFloor: number
  isSelfServedSegment: boolean
  minSeatsToCharge: number
  country: string
}

type F25PricingProviderProps = {
  children: React.ReactNode | React.ReactNode[]
}

const INITIAL_COUNTRY_FLOOR = 10

const F25PricingContext = React.createContext<F25PricingContextType>({
  allPlans: {
    f25Plans: [],
    f25Addons: [],
  },
  togglePlanSelect: () => {},
  setVariantForPlan: () => {},
  period: 'month',
  setPeriod: () => {},
  seats: INITIAL_COUNTRY_FLOOR,
  setSeats: () => {},
  configPlan: {
    bundle: 'essentials',
    recommendedAddons: [],
    planType: PRO_PLAN_TYPE,
    planVersion: 'business',
  },
  setConfigPlan: () => {},
  isYearly: () => false,
  computePerSeatPrice: () => 0,
  computeTotalPrice: () => 0,
  getSubscriptionItems: () => [],
  countryFloor: INITIAL_COUNTRY_FLOOR,
  isSelfServedSegment: false,
  minSeatsToCharge: 0,
  country: '',
})

export const F25PricingProvider = ({ children }: F25PricingProviderProps) => {
  const { company } = useCurrentSession()
  const { datoPlans, country } = useDatoPlans()
  const countryFloor = getFloorByCountry(country)
  const selfServedMax = getSelfServedByCountry(country)
  const planDetails = getPlanByIndustry(company.industry || 'other')
  const planVersion = getPlanTypeByCountry(country)
  const [period, setPeriod] = useState<PeriodUnit>('month')
  const [seats, setSeats] = React.useState<number>(countryFloor)
  const [allPlans, setAllPlans] = useState<F25PlanDTO>({
    f25Plans: [],
    f25Addons: [],
  })
  const isSelfServedSegment = Math.max(company.numberEmployees, seats) <= selfServedMax
  const [configPlan, setConfigPlan] = React.useState<BundleConfig>({
    bundle: planDetails?.plan || 'essentials',
    recommendedAddons: planDetails?.recommended_addons || [],
    planType: PRO_PLAN_TYPE,
    planVersion,
  })
  const minSeatsToCharge = Math.max(countryFloor, seats)

  const isYearly = () => {
    return period === YEAR_PERIOD
  }

  const togglePlanSelect = (plan: Plan) => {
    plan.toggleSelection()
    setAllPlans({ ...allPlans } as F25PlanDTO)
  }

  const setVariantForPlan = (variant: VariantsEnum, plan: Plan) => {
    allPlans.f25Plans.concat(allPlans.f25Addons).forEach((auxPlan) => {
      if (plan.internalTitle === auxPlan.internalTitle) {
        auxPlan.setActiveVariant(variant)
        return
      }
    })
    setAllPlans({ ...allPlans } as F25PlanDTO)
  }

  const computePrice = (priceHandler: (plan: Plan) => number | undefined) => {
    return allPlans.f25Plans.concat(allPlans.f25Addons).reduce((acc, cur) => {
      if (cur?.isSelected) {
        const price = priceHandler(cur) || 0

        return acc + price
      }
      return acc
    }, 0)
  }

  const computePerSeatPrice = () => {
    return computePrice((cur) => cur.pricing.getSeatsPrice(period))
  }

  const computeTotalPrice = () => {
    return computePrice((cur) => {
      if (allPlans.f25Plans.includes(cur)) {
        // For main plan (starter or pro), use the countryFloor value
        return cur.pricing.getTotalPrice(period, minSeatsToCharge)
      }
      if (allPlans.f25Addons.includes(cur)) {
        // For addons, use numSeats provided by the user
        return cur.pricing.getTotalPrice(period, minSeatsToCharge)
      }
      return 0
    })
  }

  const getSubscriptionItems = () => {
    const planItems = allPlans.f25Plans
      .filter((plan) => plan.isSelected)
      .map((plan) => ({
        plan: { planId: plan.pricing.getPlanId(period) },
        quantity: minSeatsToCharge,
      }))

    const addonItems = allPlans.f25Addons
      .filter((addon) => addon.isSelected)
      .map((addon) => ({
        plan: { planId: addon.pricing.getPlanId(period) },
        quantity: addon.pricing.isPerSeat(period) ? minSeatsToCharge : 1,
      }))

    return [...planItems, ...addonItems] as ChargebeeSubscription[]
  }

  useUnsafeEffect(() => {
    if (!planDetails?.plan) return
    if (datoPlans) {
      const plans = getF25Plans(datoPlans, country)
      const plansByIndustry = plans.f25Plans.filter((f25Plan) =>
        f25Plan.internalTitle.toLowerCase().includes(planDetails.plan)
      )
      plans.f25Addons
        .concat(plansByIndustry)
        .forEach((plan) =>
          plan.setActiveVariant(capitalizeFirstLetter(planVersion) as VariantsEnum)
        )
      plansByIndustry.find((plan) => plan.planType === PRO_PLAN_TYPE)?.select()
      setAllPlans({ f25Addons: plans.f25Addons, f25Plans: plansByIndustry })
    }
  }, [datoPlans, country])

  return (
    <F25PricingContext.Provider
      value={{
        allPlans,
        togglePlanSelect,
        setVariantForPlan,
        period,
        setPeriod,
        seats,
        setSeats,
        configPlan,
        setConfigPlan,
        isYearly,
        computePerSeatPrice,
        computeTotalPrice,
        getSubscriptionItems,
        countryFloor,
        isSelfServedSegment,
        minSeatsToCharge,
        country,
      }}
    >
      {children}
    </F25PricingContext.Provider>
  )
}

export const useF25PricingContext = () => useContext(F25PricingContext)
